/**
 * Phase 4 slice 3 — Raise an NCR. Responsive (desktop /quality/ncrs/new +
 * mobile /m/quality/ncr). HSE/site can raise; closure happens on the detail page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Plus, X, FileWarning } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const CATEGORIES = ['Material', 'Workmanship', 'Design Deviation', 'Documentation', 'Process', 'Test Failure', 'Other'];
const SOURCES = ['Site Inspection', 'Client Inspection', 'Internal Audit', 'Material Test', 'Third-Party Audit', 'Other'];
const SEVERITIES = [
  { v: 'low', label: 'Low', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  { v: 'medium', label: 'Medium', tone: 'bg-amber-50 text-amber-700 border-amber-300' },
  { v: 'high', label: 'High', tone: 'bg-orange-50 text-orange-700 border-orange-300' },
  { v: 'critical', label: 'Critical', tone: 'bg-red-50 text-red-700 border-red-300' },
];

const Label = ({ children }) => (
  <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">{children}</label>
);

export default function RaiseNCR() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: '', title: '', category: 'Workmanship', severity: 'medium',
    source: 'Site Inspection', location_on_site: '', description: '', immediate_containment: '',
  });
  const [factors, setFactors] = useState([]);
  const [factorInput, setFactorInput] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/projects').then((p) => {
      setProjects(p);
      if (p.length) setForm((f) => ({ ...f, project_id: p[0].id }));
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFactor = () => {
    const v = factorInput.trim();
    if (v && !factors.includes(v)) setFactors([...factors, v]);
    setFactorInput('');
  };

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const doc = await apiPost('/quality/ncrs', { ...form, contributing_factors: factors, photos_base64: photos });
      toast.success(`NCR ${doc.ncr_no} raised`);
      navigate(`/app/quality/ncrs/${doc.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Quality" title="Raise NCR" subtitle="Log a non-conformance so it can be investigated, corrected and verified" />

      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="raise-ncr-form">
        <div>
          <Label>Project</Label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="ncr-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>

        <div>
          <Label>Title</Label>
          <input value={form.title} onChange={set('title')} placeholder="e.g. Honeycombing in column C12" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ncr-title" />
        </div>

        <div>
          <Label>Category</Label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, category: c })}
                className={`px-2 py-2.5 rounded-sm text-[11px] font-medium border transition-colors ${form.category === c ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                data-testid={`ncr-category-${c.replace(/\s/g, '-').toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Severity</Label>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <button key={s.v} type="button" onClick={() => setForm({ ...form, severity: s.v })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-all ${form.severity === s.v ? `${s.tone} ring-2 ring-offset-1 ring-current` : 'bg-white text-slate-500 border-slate-300'}`}
                data-testid={`ncr-severity-${s.v}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Source</Label>
            <select value={form.source} onChange={set('source')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="ncr-source">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label>Location on site</Label>
            <input value={form.location_on_site} onChange={set('location_on_site')} placeholder="e.g. Block A · Col C12" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ncr-location" />
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <textarea value={form.description} onChange={set('description')} rows={3} placeholder="What is non-conforming, and against which spec / drawing?" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ncr-description" />
        </div>

        <div>
          <Label>Immediate containment (optional)</Label>
          <textarea value={form.immediate_containment} onChange={set('immediate_containment')} rows={2} placeholder="What was done right away to stop it getting worse" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ncr-containment" />
        </div>

        <div>
          <Label>Contributing factors</Label>
          <div className="flex gap-2 mb-2">
            <input value={factorInput} onChange={(e) => setFactorInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFactor(); } }} placeholder="e.g. poor vibration" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ncr-factor-input" />
            <button type="button" onClick={addFactor} className="px-3 py-2 bg-slate-100 rounded-sm hover:bg-slate-200" data-testid="ncr-factor-add"><Plus size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {factors.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-sm">
                {f}<button type="button" onClick={() => setFactors(factors.filter((_, j) => j !== i))}><X size={11} /></button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <Label>Photos</Label>
          <PhotoCaptureField photos={photos} setPhotos={setPhotos} />
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium"
          data-testid="ncr-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <FileWarning size={16} />}
          {saving ? 'Submitting…' : 'Raise NCR'}
        </button>
      </div>
    </div>
  );
}
